import { ActionList, Button, Popover } from "@shopify/polaris";
import {
    CircleCancelMajorMonotone,
    CircleTickMajorMonotone,
    EditMajorMonotone,
    PauseCircleMajorMonotone,
    PlayCircleMajorMonotone,
    RepeatOrderMajorMonotone,
    VocabularyMajorMonotone,
    RefreshMajorMonotone,
    DuplicateMinor,
} from "@shopify/polaris-icons";
import { get } from "lodash";
import React, { Fragment, useCallback, useReducer } from "react";
import { useAppContext } from "../../context";
import { arrInvalid, checkRole, getParamByRole, reducerFn } from "../../helper";
import history from "../../history";
import { AMAZON_IDEA_STATUS } from "../../variable";
import AmazonIdeaAssign from "./actions/amazon-idea-assign";
import AmazonIdeaDelete from "./actions/amazon-idea-delete";
import AmazonIdeaDeny from "./actions/amazon-idea-deny";
import AmazonIdeaDetail from "./actions/amazon-idea-detail";
import AmazonIdeaAccept from "./actions/amazon-idea-accept";
import AmazonIdeaPick from "./actions/amazon-idea-pick";
import AmazonIdeaStart from "./actions/amazon-idea-start";
import AmazonIdeaReject from "./actions/amazon-idea-reject";
import { isAuthorOrPicker, isStatus } from "./helper";
import AmazonIdeaMarkDone from "./actions/amazon-idea-mark-done";
import AmazonIdeaUpdateKPI from "./actions/amazon-idea-update-kpi";
import AmazonIdeaMarkDoneV2 from "./actions/amazon-idea-mark-done-v2";
import AmazonIdeaUpdateProductStatus from "./actions/amazon-idea-update-product-status";
import AmazonIdeaDuplication from "./actions/amazon-idea-duplication";
import MarkAmazonIdeaStatusDone from "./actions/amazon-idea-mark-status-done";
import AmazonIdeaUpdateStore from "./actions/amazin-idea-update-store";

export default function AmazonIdeaAction({ node }) {
    const [state, setState] = useReducer(reducerFn, {
        open: false,
        action: null,
        active: false,
    });

    const { currentUser } = useAppContext();
    const {
        isSeller,
        isChannelLeader,
        isChannelSeller,
        isChannelIdea,
        isChannelSupporter,
        isChannelSupportLeader,
    } = checkRole(currentUser);
    const isLeader = isSeller || isChannelLeader || isChannelSupportLeader;

    const pathname = getParamByRole(currentUser);

    const leader =
        isSeller || isChannelLeader || isChannelSeller || isChannelIdea;

    const handleClose = useCallback(() => {
        setState({ open: false, active: false, action: null });
    }, []);

    const handleToggle = useCallback(() => {
        setState((p) => ({
            ...p,
            active: !p.active,
        }));
    }, []);

    const handleClick = useCallback(
        (type) => () => {
            setState({ open: true, action: type });
        },
        [],
    );

    const props = {
        node,
        ids: [node.id],
        open: state.open,
        onClose: handleClose,
    };

    const isDoing = isStatus(node, [AMAZON_IDEA_STATUS.Doing]);
    const isDone = isStatus(node, [AMAZON_IDEA_STATUS.Done]);
    // const isPending = isStatus(node, [AMAZON_IDEA_STATUS.Pending]);
    const isPartiallyCompleted = isStatus(node, [
        AMAZON_IDEA_STATUS["Partially Completed"],
    ]);
    const isNeedReview = isStatus(node, [AMAZON_IDEA_STATUS["Need Review"]]);
    const isRejected = isStatus(node, [AMAZON_IDEA_STATUS.Rejected]);

    // const isPendingOrDenied = isStatus(node, [
    // AMAZON_IDEA_STATUS.Pending,
    const isPartiallyCompletedOrDenied = isStatus(node, [
        AMAZON_IDEA_STATUS["Partially Completed"],
        AMAZON_IDEA_STATUS.Denied,
    ]);

    const isPicked = !!get(node, "picker");
    const supporterCanRU =
        isChannelSupporter && isAuthorOrPicker(node, currentUser, false); // read - update

    const actions = [
        {
            content: "View Detail",
            icon: VocabularyMajorMonotone,
            onAction: handleClick("view"),
        },
        isAuthorOrPicker(node, currentUser) && {
            content: "Update",
            icon: EditMajorMonotone,
            onAction: () => {
                history.push(`/${pathname}/amazon-ideas/${node.id}/update`);
            },
        },
        isLeader &&
            !isPicked &&
            isPartiallyCompletedOrDenied && {
                content: "Assign",
                icon: RepeatOrderMajorMonotone,
                onAction: handleClick("assign"),
            },
        isLeader && {
            content: "Update KPI",
            icon: RefreshMajorMonotone,
            onAction: handleClick("updateKPI"),
        },
        isChannelSupporter &&
            !isPicked &&
            isPartiallyCompletedOrDenied && {
                content: "Pick",
                icon: RepeatOrderMajorMonotone,
                onAction: handleClick("pick"),
            },
        isNeedReview &&
            leader && {
                content: "Accept",
                icon: CircleTickMajorMonotone,
                onAction: handleClick("accept"),
            },
        isNeedReview &&
            leader && {
                content: "Reject",
                icon: CircleCancelMajorMonotone,
                onAction: handleClick("reject"),
            },
        (leader || isChannelSupportLeader || isChannelSupporter) && {
            content: "Update Store",
            icon: RefreshMajorMonotone,
            onAction: handleClick("updateStore"),
        },
        isDoing &&
            supporterCanRU && {
                content: "Mark Done",
                icon: CircleTickMajorMonotone,
                onAction: handleClick("markDone"),
            },
        (isPartiallyCompleted || isRejected) &&
            supporterCanRU && {
                content: "Mark Status Done",
                icon: CircleTickMajorMonotone,
                onAction: handleClick("markStatusDone"),
            },
        isDone && {
            content: "Update Product Status",
            icon: CircleTickMajorMonotone,
            onAction: handleClick("updateProductStatus"),
        },
        isPicked &&
            isPartiallyCompleted &&
            supporterCanRU && {
                content: "Start",
                icon: PlayCircleMajorMonotone,
                onAction: handleClick("start"),
            },
        isPartiallyCompleted &&
            supporterCanRU && {
                content: "Deny",
                icon: PauseCircleMajorMonotone,
                onAction: handleClick("deny"),
            },
        leader && {
            content: "Clone",
            icon: DuplicateMinor,
            onAction: handleClick("clone"),
        },
        isAuthorOrPicker(node, currentUser) && {
            content: "Delete",
            icon: CircleCancelMajorMonotone,
            onAction: handleClick("delete"),
        },
    ].filter(Boolean);

    const actionMarkup = {
        view: <AmazonIdeaDetail {...props} />,
        assign: <AmazonIdeaAssign {...props} />,
        pick: <AmazonIdeaPick {...props} />,
        accept: <AmazonIdeaAccept {...props} />,
        deny: <AmazonIdeaDeny {...props} />,
        start: <AmazonIdeaStart {...props} />,
        markStatusDone: <MarkAmazonIdeaStatusDone {...props} />,
        delete: <AmazonIdeaDelete {...props} />,
        reject: <AmazonIdeaReject {...props} />,
        markDone: <AmazonIdeaMarkDoneV2 {...props} />,
        updateKPI: <AmazonIdeaUpdateKPI {...props} />,
        updateProductStatus: <AmazonIdeaUpdateProductStatus {...props} />,
        clone: <AmazonIdeaDuplication {...props} />,
        updateStore: <AmazonIdeaUpdateStore {...props} />,
    };

    const activator = (
        <Button
            children="Actions"
            disclosure
            onClick={handleToggle}
            disabled={arrInvalid(actions)}
        />
    );

    return (
        <Fragment>
            <Popover
                activator={activator}
                onClose={handleToggle}
                active={state.active}
            >
                <ActionList items={actions} />
            </Popover>

            {actionMarkup[state.action]}
        </Fragment>
    );
}
